import { NgOptimizedImage, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import {
  RdsAvatarComponent,
  RdsAvatarImageDirective,
  RdsButtonComponent,
  RdsDividerComponent,
  RdsDrawerComponent,
  RdsDrawerContainerComponent,
  RdsDrawerContentComponent,
  RdsDropdownDirective,
  RdsDropdownMenuDirective,
  RdsDropdownToggleDirective,
  RdsHeaderActionsDirective,
  RdsHeaderAppNameDirective,
  RdsHeaderComponent,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsProgressSpinnerComponent,
  RdsTextButtonComponent,
} from '@rds/angular-components';
import { filter } from 'rxjs';

import {
  AuthService,
  ChartFilterMenuComponent,
  DestinationFrontendEnum,
  NotificationsDropdownComponent,
  NotificationsService,
  RoleType,
  ViewportService,
  environment,
} from '@bookly/shared';

import { DashboardNavComponent } from './components/dashboard-nav/dashboard-nav.component';

@Component({
  selector: 'bad-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RdsAvatarComponent,
    RdsAvatarImageDirective,
    RouterLink,
    RdsIconComponent,
    RouterOutlet,
    NgOptimizedImage,
    NotificationsDropdownComponent,
    RdsProgressSpinnerComponent,
    ChartFilterMenuComponent,
    NgStyle,
    RdsHeaderComponent,
    RdsDividerComponent,
    RdsHeaderAppNameDirective,
    RdsHeaderActionsDirective,
    RdsDropdownDirective,
    RdsDropdownToggleDirective,
    RdsTextButtonComponent,
    RdsDropdownMenuDirective,
    RdsIconButtonComponent,
    RdsDrawerContainerComponent,
    RdsDrawerComponent,
    RdsDrawerContentComponent,
    RdsButtonComponent,
    DashboardNavComponent,
  ],
})
export class DashboardComponent implements OnInit {
  readonly #authService = inject(AuthService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #viewport = inject(ViewportService);
  /** Below 1024px the sidenav is replaced by an off-canvas drawer. */
  protected readonly isCompact = this.#viewport.isCompact;
  protected readonly isNavDrawerOpen = signal(false);
  protected readonly user = this.#authService.user;
  protected readonly development = environment.shared.development;
  protected readonly isLoading = this.#authService.isLoginPending;
  protected readonly RoleType = RoleType;
  protected readonly router = inject(Router);
  protected role: RoleType | null = null;

  @ViewChild(RdsDropdownDirective)
  userMenuDropdown?: RdsDropdownDirective;

  constructor() {
    // Without this the drawer stays open on top of the page just navigated to.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeNavDrawer());
  }

  ngOnInit() {
    this.role = this.#activatedRoute.snapshot.data['role'] as RoleType | null;
    this.#notificationsService
      .getNotifications(DestinationFrontendEnum.Admin)
      .subscribe();
  }

  protected toggleNavDrawer() {
    this.isNavDrawerOpen.update(open => !open);
  }

  protected closeNavDrawer() {
    this.isNavDrawerOpen.set(false);
  }

  protected logout() {
    // Optional: on compact viewports logout lives in the drawer and the avatar
    // dropdown is not rendered at all.
    this.userMenuDropdown?.hide();
    this.closeNavDrawer();
    void this.#authService.logout();
  }

  protected title(): string {
    if (this.role === RoleType.ADMIN) {
      return 'Admin Dashboard';
    }
    if (this.role === RoleType.ROOM_HOST) {
      return 'Room Host Dashboard';
    }
    if (this.role === RoleType.SERVICE_PROVIDER) {
      return 'Service Provider Dashboard';
    }
    return 'Dashboard';
  }

  protected showChartsMenu(): boolean {
    return this.router.url.indexOf('statistics') > -1;
  }
}
